/* eslint-disable react/jsx-no-bind */
import {
  CheckCircle,
  Settings,
  RemoveCircle,
  ViewModule,
  TableRows,
  ContentCopy,
  Visibility,
  ArrowBack,
  ArrowForward,
  Cancel,
  Close,
} from '@mui/icons-material';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';
import kycService from '../ManualKYC/kycService';

import customerService from './customerService';

const Customer = () => {
  const [apiResponse, setApiResponse] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [todayRegistrationsCount, setTodayRegistrationsCount] = useState(0);

  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [statusFilter, setStatusFilter] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isWidgetView, setIsWidgetView] = useState(true);
  const [todayRegistrations, setTodayRegistrations] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Local state for inputs (only applied on search click)
  const [localSearch, setLocalSearch] = useState('');
  const [localSearchType, setLocalSearchType] = useState('name');
  const [localStatusFilter, setLocalStatusFilter] = useState('');
  const [localTodayRegistrations, setLocalTodayRegistrations] = useState(false);
  const [localFromDate, setLocalFromDate] = useState(null);
  const [localToDate, setLocalToDate] = useState(null);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [kycDocuments, setKycDocuments] = useState(null);
  const [rejectionCategory, setRejectionCategory] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);
  const [imageBrightness, setImageBrightness] = useState(100);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [aadhaarError, setAadhaarError] = useState('');
  const [aadhaarVerified, setAadhaarVerified] = useState(false);
  const [kycLoadingUserId, setKycLoadingUserId] = useState(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();

  // Copy to clipboard function
  const copyToClipboard = (text, type) => {
    if (text && text !== 'N/A') {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success(`${type} copied to clipboard!`);
        })
        .catch(() => {
          toast.error('Failed to copy to clipboard');
        });
    }
  };

  // Auto-detect search type based on input
  const detectSearchType = (value) => {
    const trimmedValue = value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Phone regex: allows digits with optional +, spaces, dashes, parentheses
    // After cleaning, should be 7-15 digits (typical phone number length)
    const cleanedPhone = trimmedValue.replace(/[\s\-()+]/g, '');
    const phoneRegex = /^[0-9]{7,15}$/;

    if (emailRegex.test(trimmedValue)) {
      return 'email';
    } else if (phoneRegex.test(cleanedPhone)) {
      return 'phone';
    } else {
      return 'name';
    }
  };

  // Handle paste event for auto-detection
  const handlePaste = (e) => {
    setTimeout(() => {
      const pastedValue = e.target.value;
      if (pastedValue.trim()) {
        const detectedType = detectSearchType(pastedValue.trim());
        setLocalSearchType(detectedType);
      }
    }, 10);
  };

  // Copyable text component
  const CopyableText = ({ text, type, children }) => {
    const [, setShowCopy] = useState(false);

    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          position: 'relative',
          cursor: text && text !== 'N/A' ? 'pointer' : 'default',
          '&:hover .copy-icon': {
            opacity: text && text !== 'N/A' ? 1 : 0,
          },
        }}
        onMouseEnter={() => setShowCopy(true)}
        onMouseLeave={() => setShowCopy(false)}
        onClick={() => copyToClipboard(text, type)}
      >
        {children}
        {text && text !== 'N/A' && (
          <ContentCopy
            className="copy-icon"
            sx={{
              ml: 1,
              fontSize: '14px',
              opacity: 0,
              transition: 'opacity 0.2s',
              color: '#666',
            }}
          />
        )}
      </Box>
    );
  };

  // Search type options
  const searchTypeOptions = [
    { value: 'name', label: 'Name' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
  ];

  // Status options for filtering
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'approved', label: 'Approved' },
    { value: 'reject', label: 'Rejected' },
    { value: 'kyc required', label: 'KYC Required' },
    { value: 'in review', label: 'In Review' },
    { value: 'kyc not done', label: 'KYC Not Done' },
  ];

  useEffect(() => {
    // Check URL parameters for filter
    const urlParams = new URLSearchParams(location.search);
    const filterParam = urlParams.get('filter');
    if (filterParam) {
      setStatusFilter(filterParam);
      setLocalStatusFilter(filterParam);
      fetchCustomers(1, rowsPerPage, filterParam, '', 'name', null, '', '');
    } else {
      fetchCustomers(1, rowsPerPage, '', '', 'name', null, '', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, rowsPerPage]);

  // Transform API response to flatten the nested structure
  const transformCustomerData = (data) => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => {
      const user = item.user || {};
      const userKyc = item.userKyc || {};

      // Normalize KYC status for comparison
      const kycStatus = userKyc.status
        ? String(userKyc.status).toLowerCase().trim()
        : '';

      return {
        userId: user.userId,
        name: user.name,
        email: user.email,
        phone: user.phone,
        isActive: user.isActive,
        gender: userKyc.json?.gender,
        dateOfBirth: userKyc.json?.dob,
        createdOn: user.createdOn,
        updatedOn: user.updatedOn,
        isKycCompleted: kycStatus === 'approved',
        kycStatus: userKyc.status,
        kycId: userKyc.kycId,
        kycType: userKyc.type,
        aadhaarNumber: userKyc.aadhaarNumber,
        rejectReason: userKyc.rejectReason,
        kycCreatedOn: userKyc.createdOn,
        kycUpdatedOn: userKyc.updatedOn,
        // Address fields from KYC JSON
        address1: userKyc.json?.address1,
        address2: userKyc.json?.address2,
        pincode: userKyc.json?.pincode,
        city: userKyc.json?.local,
        district: userKyc.json?.dist,
        state: userKyc.json?.state,
        country: userKyc.json?.country,
        careOf: userKyc.json?.co,
        house: userKyc.json?.house,
        street: userKyc.json?.street,
        // KYC Image (DigiLocker: base64 in json; manual: signed URLs on userKyc)
        base64Image: userKyc.json?.base64_image,
        userImageUrl: userKyc.userImageUrl,
        aadharFrontUrl: userKyc.aadharFrontUrl,
        aadharBackUrl: userKyc.aadharBackUrl,
      };
    });
  };
  // Render KYC status as a styled Chip component (matches API: "approved", "in review", "kyc not done", "reject"/"rejected")
  const renderKycChip = (status, size = 'small') => {
    const s = (status || '').toString().toLowerCase().trim();
    if (s === 'approved') {
      return (
        <Chip
          icon={
            <CheckCircle
              sx={{
                width: size === 'small' ? 16 : 18,
                height: size === 'small' ? 16 : 18,
              }}
            />
          }
          label="Approved"
          size={size}
          sx={{
            backgroundColor: '#e8f5e8',
            color: '#2e7d32',
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '13px',
            '& .MuiChip-icon': {
              width: size === 'small' ? 16 : 18,
              height: size === 'small' ? 16 : 18,
            },
          }}
        />
      );
    }

    if (s === 'reject' || s === 'rejected') {
      return (
        <Chip
          icon={
            <Cancel
              sx={{
                width: size === 'small' ? 16 : 18,
                height: size === 'small' ? 16 : 18,
              }}
            />
          }
          label="Rejected"
          size={size}
          sx={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '13px',
            '& .MuiChip-icon': {
              width: size === 'small' ? 16 : 18,
              height: size === 'small' ? 16 : 18,
            },
          }}
        />
      );
    }
    if (s === 'kyc required') {
      return (
        <Chip
          icon={
            <RemoveCircle
              sx={{
                width: size === 'small' ? 16 : 18,
                height: size === 'small' ? 16 : 18,
              }}
            />
          }
          label="KYC Required"
          size={size}
          sx={{
            backgroundColor: '#fff8e1',
            color: '#f57c00',
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '13px',
            '& .MuiChip-icon': {
              width: size === 'small' ? 16 : 18,
              height: size === 'small' ? 16 : 18,
            },
          }}
        />
      );
    }
    if (s === 'kyc required') {
      return (
        <Chip
          icon={
            <RemoveCircle
              sx={{
                width: size === 'small' ? 16 : 18,
                height: size === 'small' ? 16 : 18,
              }}
            />
          }
          label="KYC Required"
          size={size}
          sx={{
            backgroundColor: '#fff8e1',
            color: '#f57c00',
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '13px',
            '& .MuiChip-icon': {
              width: size === 'small' ? 16 : 18,
              height: size === 'small' ? 16 : 18,
            },
          }}
        />
      );
    }

    if (s === 'in review') {
      return (
        <Chip
          icon={
            <RemoveCircle
              sx={{
                width: size === 'small' ? 16 : 18,
                height: size === 'small' ? 16 : 18,
              }}
            />
          }
          label="In Review"
          size={size}
          sx={{
            backgroundColor: '#fff8e1',
            color: '#f57c00',
            fontWeight: 500,
            fontSize: size === 'small' ? '11px' : '13px',
            '& .MuiChip-icon': {
              width: size === 'small' ? 16 : 18,
              height: size === 'small' ? 16 : 18,
            },
          }}
        />
      );
    }

    // "kyc not done" or any other / empty
    return (
      <Chip
        icon={
          <RemoveCircle
            sx={{
              width: size === 'small' ? 16 : 18,
              height: size === 'small' ? 16 : 18,
            }}
          />
        }
        label="KYC Not Done"
        size={size}
        sx={{
          backgroundColor: '#eceff1',
          color: '#34543dff',
          fontWeight: 500,
          fontSize: size === 'small' ? '11px' : '13px',
          '& .MuiChip-icon': {
            width: size === 'small' ? 16 : 18,
            height: size === 'small' ? 16 : 18,
          },
        }}
      />
    );
  };
  const fetchCustomers = async (
    page = 1,
    page_size = 10,
    kycFilter = '',
    searchQuery = '',
    searchField = '',
    todayRegs = false,
    fromDateParam = '',
    toDateParam = '',
  ) => {
    setLoading(true);
    try {
      const res = await customerService.fetchCustomers(
        page,
        page_size,
        kycFilter,
        searchQuery,
        searchField,
        todayRegs,
        fromDateParam,
        toDateParam,
      );

      const responseData = res.data || {};
      const payload = responseData.data || responseData;
      const customers =
        payload.customers ||
        responseData.customers ||
        (Array.isArray(payload) ? payload : []);
      const total =
        payload.total ??
        responseData.total ??
        payload.totalCount ??
        responseData.totalCount ??
        0;
      const todayRegistrationTotal =
        payload.todayRegistrations ??
        responseData.todayRegistrations ??
        payload.todayRegistrationsCount ??
        responseData.todayRegistrationsCount ??
        0;

      // Transform the nested data structure
      const transformedData = transformCustomerData(customers);

      setApiResponse(transformedData);
      setTotalCount(total);
      setTodayRegistrationsCount(todayRegistrationTotal);
    } catch (err) {
      toast.error('Error fetching customers');
      logger.error('Customer error', err);
      setApiResponse([]);
      setTotalCount(0);
    }
    setLoading(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    // Auto-detect search type for longer inputs (likely pasted)
    if (value.trim().length > 2) {
      const detectedType = detectSearchType(value.trim());
      if (detectedType !== localSearchType) {
        setLocalSearchType(detectedType);
      }
    }
  };

  const handleStatusFilterChange = (e) => {
    setLocalStatusFilter(e.target.value);
  };

  const handleApplyFilters = () => {
    setPage(0);
    // Convert dayjs dates to strings for API
    const fromDateStr = localFromDate ? localFromDate.format('YYYY-MM-DD') : '';
    const toDateStr = localToDate ? localToDate.format('YYYY-MM-DD') : '';

    // Sync local state to main state
    setSearch(localSearch);
    setSearchType(localSearchType);
    setStatusFilter(localStatusFilter);
    setTodayRegistrations(localTodayRegistrations);
    setFromDate(fromDateStr);
    setToDate(toDateStr);

    // Use the selected searchType, but if search value matches a different pattern, use that
    let currentSearchType = localSearchType;
    if (localSearch.trim()) {
      const detectedType = detectSearchType(localSearch.trim());
      // If detected type differs from selected, use detected (user likely pasted something)
      // But if user explicitly selected a type and it's valid, respect their choice
      if (detectedType !== localSearchType && localSearchType === 'name') {
        // Only override if user hasn't explicitly selected email or phone
        currentSearchType = detectedType;
        setLocalSearchType(detectedType);
        setSearchType(detectedType);
      }
    }
    fetchCustomers(
      1,
      rowsPerPage,
      localStatusFilter,
      localSearch.trim(),
      currentSearchType,
      null,
      fromDateStr,
      toDateStr,
    );
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setLocalSearchType('name');
    setLocalStatusFilter('');
    setLocalTodayRegistrations(false);
    setLocalFromDate(null);
    setLocalToDate(null);
    setSearch('');
    setSearchType('name');
    setStatusFilter('');
    setTodayRegistrations(false);
    setFromDate('');
    setToDate('');
    setPage(0);
    fetchCustomers(1, rowsPerPage, '', '', 'name', null, '', '');
  };

  const handleSearchTypeChange = (e) => {
    setLocalSearchType(e.target.value);
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCustomer(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Use the selected searchType when paginating
    fetchCustomers(
      newPage + 1,
      rowsPerPage,
      statusFilter,
      search.trim(),
      searchType,
      todayRegistrations ? true : null,
      fromDate,
      toDate,
    );
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Use the selected searchType when changing rows per page
    fetchCustomers(
      1,
      newRowsPerPage,
      statusFilter,
      search.trim(),
      searchType,
      todayRegistrations ? true : null,
      fromDate,
      toDate,
    );
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCustomer(null);
  };

  const handleDelete = () => {
    if (!selectedCustomer) return;

    setLoading(true);
    customerService
      .deleteCustomer(selectedCustomer.userId)
      .then(() => {
        toast.success('Customer deleted successfully');
        return fetchCustomers(
          page + 1,
          rowsPerPage,
          statusFilter,
          search.trim(),
          searchType,
          todayRegistrations ? true : null,
          fromDate,
          toDate,
        );
      })
      .then(() => handleMenuClose())
      .catch((error) => {
        toast.error('Error deleting customer');
        logger.error('Customer error', error);
      })
      .finally(() => setLoading(false));
  };

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    setModalOpen(true);
  };

  const handleManualKyc = async (userId) => {
    setKycLoadingUserId(userId);
    try {
      const response = await kycService.fetchKYCByUserId(userId);
      const kycRecord = response.data;

      if (!kycRecord) {
        toast.error(response.message || 'No KYC record found for this user');
        return;
      }

      const customer = apiResponse.find((c) => c.userId === userId);
      setSelectedCustomer({ ...customer, ...kycRecord });

      const kycData = {
        documentName: kycRecord.json?.name || 'N/A',
        documentDob: kycRecord.json?.dob || 'N/A',
        documentId: kycRecord.aadhaarNumber,
        documentImages: [
          {
            url: kycRecord.aadharFrontUrl,
            label: 'Aadhaar Card - Front Side',
          },
          {
            url: kycRecord.aadharBackUrl,
            label: 'Aadhaar Card - Back Side',
          },
          {
            url: kycRecord.userImageUrl,
            label: 'Selfie Verification Photo',
          },
        ].filter((img) => img.url),
      };

      setKycDocuments(kycData);
      setAadhaarNumber(kycRecord.aadhaarNumber || '');
      setKycModalOpen(true);
    } catch (error) {
      logger.error('KYC fetch error:', error);
      toast.error('Error fetching KYC data');
    } finally {
      setKycLoadingUserId(null);
    }
  };

  const handleKycApproval = async () => {
    if (window.confirm('Are you sure you want to approve this KYC?')) {
      try {
        setLoading(true);
        await kycService.approveRejectKYC(selectedCustomer.kycId, true);
        toast.success('KYC approved successfully!');
        setKycModalOpen(false);
        await fetchCustomers(
          page + 1,
          rowsPerPage,
          statusFilter,
          search.trim(),
          searchType,
          todayRegistrations ? true : null,
          fromDate,
          toDate,
        );
      } catch (error) {
        toast.error('Failed to approve KYC');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleKycRejection = async () => {
    if (!rejectionCategory) {
      toast.error('Please select a rejection category');
      return;
    }
    if (window.confirm('Are you sure you want to reject this KYC?')) {
      try {
        setLoading(true);
        const finalReason = `${rejectionCategory}: ${rejectionReason.trim()}`;
        await kycService.approveRejectKYC(
          selectedCustomer.kycId,
          false,
          finalReason,
        );
        toast.success('KYC rejected successfully');
        setKycModalOpen(false);
        setRejectionCategory('');
        setRejectionReason('');
        await fetchCustomers(
          page + 1,
          rowsPerPage,
          statusFilter,
          search.trim(),
          searchType,
          todayRegistrations ? true : null,
          fromDate,
          toDate,
        );
      } catch (error) {
        toast.error('Failed to reject KYC');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetImageControls = () => {
    setAadhaarNumber('');
    setAadhaarError('');
    setAadhaarVerified(false);
    setCurrentImageIndex(0);
    setImageZoom(1);
    setImageRotation(0);
    setImageBrightness(100);
    setRejectionCategory('');
    setRejectionReason('');
  };

  const validateAadhaar = (aadhaar) => {
    const aadhaarRegex = /^[2-9]{1}[0-9]{3}\s[0-9]{4}\s[0-9]{4}$/;
    return aadhaarRegex.test(aadhaar);
  };

  const handleAadhaarVerify = () => {
    if (!aadhaarNumber.trim()) {
      setAadhaarError('Aadhaar number is required');
      return;
    }
    if (!validateAadhaar(aadhaarNumber)) {
      setAadhaarError('Please enter valid Aadhaar number (XXXX XXXX XXXX)');
      return;
    }
    setAadhaarError('');
    setAadhaarVerified(true);
    toast.success('Aadhaar number verified successfully!');
  };

  const formatAadhaar = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,4})(\d{0,4})(\d{0,4})$/);
    if (match) {
      return [match[1], match[2], match[3]].filter(Boolean).join(' ');
    }
    return value;
  };

  const renderKycStatusChip = (status, size = 'small') => {
    const s = (status || '').toString().toLowerCase().trim();
    if (s === 'approved') {
      return (
        <Chip
          label="Approved"
          size={size}
          sx={{
            height: size === 'small' ? 20 : 24,
            fontSize: size === 'small' ? '11px' : '12px',
            bgcolor: colors.greenAccent[100],
            color: 'white',
            fontWeight: 500,
          }}
        />
      );
    }
    if (s === 'reject' || s === 'rejected') {
      return (
        <Chip
          label="Rejected"
          size={size}
          sx={{
            height: size === 'small' ? 20 : 24,
            fontSize: size === 'small' ? '11px' : '12px',
            bgcolor: colors.redAccent[100],
            color: 'white',
            fontWeight: 500,
          }}
        />
      );
    }
    if (s === 'in review') {
      return (
        <Chip
          label="In Review"
          size={size}
          sx={{
            height: size === 'small' ? 20 : 24,
            fontSize: size === 'small' ? '11px' : '12px',
            bgcolor: colors.blueAccent[100],
            color: 'white',
            fontWeight: 500,
          }}
        />
      );
    }
    return (
      <Chip
        label="KYC Not Done"
        size={size}
        sx={{
          height: size === 'small' ? 20 : 24,
          fontSize: size === 'small' ? '11px' : '12px',
          bgcolor: colors.grey[500],
          color: 'white',
          fontWeight: 500,
        }}
      />
    );
  };

  const nextImage = () => {
    if (kycDocuments?.documentImages) {
      setCurrentImageIndex((prev) =>
        prev < kycDocuments.documentImages.length - 1 ? prev + 1 : 0,
      );
    }
  };

  const prevImage = () => {
    if (kycDocuments?.documentImages) {
      setCurrentImageIndex((prev) =>
        prev > 0 ? prev - 1 : kycDocuments.documentImages.length - 1,
      );
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <span className="global_loader"></span>
      </div>
    );
  }

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: { xs: 0.5, sm: 1 },
          margin: { xs: 1, sm: 2 },
          marginLeft: { xs: 1, sm: 2, md: 4 },
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Customer Management</h3>
          </Box>
          <Divider />
          <Box sx={{ padding: { xs: 1, sm: 2, md: 4 } }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              {/* First Row */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Search By"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={localSearchType}
                  onChange={handleSearchTypeChange}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const option = searchTypeOptions.find(
                        (opt) => opt.value === selected,
                      );
                      return option ? option.label : selected;
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                      transform: 'translate(14px, 10px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                  }}
                >
                  {searchTypeOptions.map((option) => (
                    <DropdownItem key={option.value} value={option.value}>
                      {option.label}
                    </DropdownItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label={`Search by ${
                    searchTypeOptions.find(
                      (opt) => opt.value === localSearchType,
                    )?.label
                  }`}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={localSearch}
                  onChange={handleSearchChange}
                  onPaste={handlePaste}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Filter by KYC Status"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={localStatusFilter}
                  onChange={handleStatusFilterChange}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === '' || !selected) {
                        return 'All Statuses';
                      }
                      const option = statusOptions.find(
                        (opt) => opt.value === selected,
                      );
                      return option ? option.label : selected;
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                      transform: 'translate(14px, 10px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                  }}
                >
                  {statusOptions.map((option) => (
                    <DropdownItem key={option.value} value={option.value}>
                      {option.label}
                    </DropdownItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={12} md={5}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DatePicker
                      label="From Date"
                      value={localFromDate}
                      onChange={(newDate) => {
                        setLocalFromDate(newDate);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'gray',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: colors.swiftcarry[100],
                            },
                          },
                        },
                      }}
                    />
                    <Typography sx={{ color: '#666', mx: 0.5 }}>—</Typography>
                    <DatePicker
                      label="To Date"
                      value={localToDate}
                      onChange={(newDate) => {
                        setLocalToDate(newDate);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'gray',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: colors.swiftcarry[100],
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>
              {/* Second Row */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{
                      bgcolor: colors.swiftcarry[100],
                      '&:hover': {
                        bgcolor: colors.swiftcarry[100],
                      },
                      height: '40px',
                      flex: 1,
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                    sx={{
                      borderColor: '#ccc',
                      color: '#666',
                      height: '40px',
                      flex: 1,
                      '&:hover': {
                        borderColor: colors.swiftcarry[100],
                        color: colors.swiftcarry[100],
                        backgroundColor: `${colors.swiftcarry[100]}10`,
                      },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant={localTodayRegistrations ? 'contained' : 'outlined'}
                  onClick={() => {
                    const newValue = !localTodayRegistrations;
                    setLocalTodayRegistrations(newValue);
                    setPage(0);
                    fetchCustomers(
                      1,
                      rowsPerPage,
                      '',
                      '',
                      '',
                      newValue,
                      '',
                      '',
                    );
                  }}
                  fullWidth
                  sx={{
                    bgcolor: localTodayRegistrations
                      ? colors.swiftcarry[100]
                      : 'transparent',
                    borderColor: colors.swiftcarry[100],
                    color: localTodayRegistrations
                      ? 'white'
                      : colors.swiftcarry[100],
                    boxShadow: localTodayRegistrations
                      ? '0 2px 4px rgba(0,0,0,0.1)'
                      : 'none',
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                      color: 'white',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    },
                    height: '40px',
                  }}
                >
                  Today's
                </Button>
              </Grid>
            </Grid>

            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Chip
                  label={`Total Records: ${totalCount}`}
                  size="small"
                  sx={{ mr: 2, p: 1 }}
                />
                <Chip
                  label={`Today Registrations: ${todayRegistrationsCount}`}
                  size="small"
                  sx={{ p: 1 }}
                />
              </Box>
              <IconButton
                onClick={() => setIsWidgetView(!isWidgetView)}
                sx={{
                  backgroundColor: isWidgetView
                    ? colors.swiftcarry[100]
                    : '#f5f5f5',
                  color: isWidgetView ? 'white' : colors.swiftcarry[100],
                  width: '40px',
                  height: '40px',
                  border: `2px solid ${colors.swiftcarry[100]}`,
                  boxShadow: '0 2px 8px rgba(0, 201, 150, 0.2)',
                  '&:hover': {
                    backgroundColor: '#000',
                    color: 'white',
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {isWidgetView ? <TableRows /> : <ViewModule />}
              </IconButton>
            </Box>

            {/* Widget View */}
            {isWidgetView && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2.5}>
                  {apiResponse.length > 0 ? (
                    apiResponse.map((customer, index) => (
                      <Grid item xs={12} sm={6} md={4} key={customer.userId}>
                        <Box
                          sx={{
                            position: 'relative',
                            height: '100%',
                            borderRadius: '16px',
                            background:
                              'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                            border: '1px solid #e2e8f0',
                            boxShadow:
                              '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                            transition: 'all 0.25s ease',
                            overflow: 'hidden',
                            '&:hover': {
                              transform: 'translateY(-4px) scale(1.01)',
                              boxShadow:
                                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                              borderColor: colors.swiftcarry[100],
                              '& .accent-bar': {
                                width: '6px',
                              },
                            },
                          }}
                        >
                          {/* Side Accent Bar */}
                          <Box
                            className="accent-bar"
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              background: `linear-gradient(180deg, ${colors.swiftcarry[100]} 0%, ${colors.swiftcarry[200]} 100%)`,
                              transition: 'width 0.25s ease',
                              zIndex: 1,
                            }}
                          />

                          <Box sx={{ p: 2.5, position: 'relative' }}>
                            {/* Top Section - Avatar & Name */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '14px',
                                  background: `linear-gradient(135deg, ${colors.swiftcarry[100]} 0%, ${colors.swiftcarry[200]} 100%)`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '22px',
                                  mr: 1.5,
                                  boxShadow: `0 4px 14px ${colors.swiftcarry[100]}40`,
                                  border: '2px solid rgba(255, 255, 255, 0.3)',
                                }}
                              >
                                {customer.name
                                  ? customer.name.charAt(0).toUpperCase()
                                  : 'U'}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <CopyableText text={customer.name} type="Name">
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '16px',
                                      mb: 0.5,
                                      color: '#0f172a',
                                      lineHeight: 1.2,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {customer.name || 'N/A'}
                                  </Typography>
                                </CopyableText>
                                <CopyableText
                                  text={customer.userId}
                                  type="User ID"
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      fontFamily: 'monospace',
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    User ID: {customer.userId}
                                  </Typography>
                                </CopyableText>
                              </Box>
                            </Box>

                            {/* Contact Info - Compact */}
                            <Box
                              sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: '10px',
                                background: 'rgba(241, 245, 249, 0.6)',
                                border: '1px solid #e2e8f0',
                              }}
                            >
                              <CopyableText text={customer.email} type="Email">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                    '&:last-child': { mb: 0 },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      bgcolor: colors.swiftcarry[100],
                                      mr: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#475569',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {customer.email || 'No email'}
                                  </Typography>
                                </Box>
                              </CopyableText>
                              <CopyableText text={customer.phone} type="Phone">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: customer.kycType ? 1 : 0,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      bgcolor: colors.swiftcarry[100],
                                      mr: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#475569',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {customer.phone || 'No phone'}
                                  </Typography>
                                </Box>
                              </CopyableText>
                              {customer.kycType && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '6px',
                                      height: '6px',
                                      borderRadius: '50%',
                                      bgcolor: colors.swiftcarry[100],
                                      mr: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#475569',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    {customer.kycType}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Bottom Section - Status & Actions */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                pt: 1.5,
                                borderTop: '1px solid #e2e8f0',
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                {renderKycChip(customer.kycStatus)}
                                {customer.kycStatus &&
                                  String(customer.kycStatus)
                                    .toLowerCase()
                                    .trim() !== 'approved' &&
                                  String(customer.kycStatus)
                                    .toLowerCase()
                                    .trim() !== 'kyc not done' &&
                                  String(customer.kycStatus)
                                    .toLowerCase()
                                    .trim() !== 'kyc required' &&
                                  String(customer.kycStatus)
                                    .toLowerCase()
                                    .trim() !== 'reject' && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() =>
                                      handleManualKyc(customer.userId)
                                    }
                                    disabled={
                                      kycLoadingUserId === customer.userId
                                    }
                                    sx={{
                                      mt: 1,
                                      bgcolor: colors.swiftcarry[100],
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      minHeight: '26px',
                                      px: 1.25,
                                      py: 0.25,
                                      borderRadius: '8px',
                                      textTransform: 'none',
                                      boxShadow: `0 2px 8px ${colors.swiftcarry[100]}50`,
                                      '&:hover': {
                                        bgcolor: colors.swiftcarry[100],
                                        boxShadow: `0 4px 12px ${colors.swiftcarry[100]}60`,
                                        transform: 'translateY(-1px)',
                                      },
                                      '&:disabled': {
                                        bgcolor: '#cbd5e1',
                                        color: '#94a3b8',
                                      },
                                      transition: 'all 0.2s ease',
                                    }}
                                  >
                                    {kycLoadingUserId === customer.userId
                                      ? 'Loading...'
                                      : 'Review KYC'}
                                  </Button>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-end',
                                  gap: 0.5,
                                  ml: 1,
                                  minWidth: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '9px',
                                      fontWeight: 500,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    Created On:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      lineHeight: 1.3,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '100%',
                                    }}
                                  >
                                    {formatToIST(customer.createdOn)}
                                  </Typography>
                                </Box>
                                <IconButton
                                  onClick={() => handleViewDetails(customer)}
                                  sx={{
                                    padding: '5px',
                                    backgroundColor: 'rgba(15, 23, 42, 0.05)',
                                    color: '#475569',
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    '&:hover': {
                                      backgroundColor: colors.swiftcarry[100],
                                      color: 'white',
                                      borderColor: colors.swiftcarry[100],
                                      transform: 'rotate(5deg) scale(1.1)',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <Visibility sx={{ width: 14, height: 14 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          sx={{ fontWeight: 400, color: '#64748b' }}
                        >
                          No customers found
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Table View */}
            {!isWidgetView && (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table
                  sx={{
                    minWidth: { xs: 800, sm: 650 },
                    '& .MuiTableCell-root': {
                      padding: { xs: '6px 4px', sm: '16px' },
                      fontSize: { xs: '11px', sm: '14px' },
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        User ID
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Email
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Phone
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        KYC Status
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        KYC type
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Created Date
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Updated Date
                      </TableCell>
                      <TableCell style={{ fontWeight: 'bold' }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiResponse.length > 0 ? (
                      apiResponse.map((row) => (
                        <TableRow key={row.userId}>
                          <TableCell>{row.userId}</TableCell>
                          <TableCell>
                            <CopyableText text={row.name} type="Name">
                              <span>{row.name || 'N/A'}</span>
                            </CopyableText>
                          </TableCell>
                          <TableCell>
                            <CopyableText text={row.email} type="Email">
                              <span>{row.email || 'N/A'}</span>
                            </CopyableText>
                          </TableCell>
                          <TableCell>
                            <CopyableText text={row.phone} type="Phone">
                              <span>{row.phone || 'N/A'}</span>
                            </CopyableText>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                              }}
                            >
                              {renderKycChip(row.kycStatus)}
                              {!row.isKycCompleted &&
                                String(row.kycStatus).toLowerCase().trim() !==
                                  'kyc not done' &&
                                String(row.kycStatus).toLowerCase().trim() !==
                                  'kyc required' &&
                                String(row.kycStatus).toLowerCase().trim() !==
                                  'reject' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleManualKyc(row.userId)}
                                  disabled={kycLoadingUserId === row.userId}
                                  sx={{
                                    bgcolor: colors.swiftcarry[100],
                                    fontSize: '11px',
                                    minHeight: '24px',
                                    py: 0.5,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                      bgcolor: colors.swiftcarry[100],
                                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                    },
                                  }}
                                >
                                  {kycLoadingUserId === row.userId
                                    ? 'Loading...'
                                    : 'Review KYC'}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                textTransform: 'capitalize',
                                color: row.kycType ? '#475569' : '#94a3b8',
                              }}
                            >
                              {row.kycType || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatToIST(row.createdOn)}</TableCell>
                          <TableCell>{formatToIST(row.updatedOn)}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={(event) => handleMenuOpen(event, row)}
                              sx={{ padding: '8px' }}
                            >
                              <Settings sx={{ width: 20, height: 20 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          No customers found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TablePagination
              rowsPerPageOptions={[5, 10, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                width: '100%',
                overflow: 'auto',
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: { xs: 1, sm: 2 },
                  paddingRight: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                  minHeight: { xs: '52px', sm: '64px' },
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-select': {
                  marginRight: { xs: 1, sm: 2 },
                  marginLeft: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: { xs: 0.5, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                },
                '& .MuiIconButton-root': {
                  padding: { xs: '4px', sm: '8px' },
                },
                '& .MuiTablePagination-input': {
                  marginRight: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-menuItem': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
              }}
            />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <DropdownItem
                onClick={() => {
                  handleViewDetails(selectedCustomer);
                  handleMenuClose();
                }}
              >
                View Details
              </DropdownItem>
              {/* <DropdownItem onClick={handleDelete}>Delete</DropdownItem> */}
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: 560 },
            maxWidth: '560px',
            bgcolor: 'background.paper',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 24px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)'
                : '0 24px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedCustomer && (
            <>
              {/* Header */}
              <Box
                sx={{
                  px: { xs: 2.5, sm: 3 },
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '-0.02em',
                      color: 'text.primary',
                    }}
                  >
                    Customer Details
                  </Typography>
                  <Chip
                    label={`#${selectedCustomer.userId}`}
                    size="small"
                    sx={{
                      height: 24,
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(0,0,0,0.06)',
                      color: 'text.secondary',
                      border: 'none',
                    }}
                  />
                </Stack>
                <IconButton
                  onClick={handleModalClose}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(0,0,0,0.04)',
                      color: 'text.primary',
                    },
                  }}
                >
                  <Close />
                </IconButton>
              </Box>

              {/* Scrollable body */}
              <Box
                sx={{
                  overflowY: 'auto',
                  flex: 1,
                  px: { xs: 2.5, sm: 3 },
                  py: 2.5,
                }}
              >
                <Stack spacing={3}>
                  {/* Profile section */}
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        color: 'text.secondary',
                        mb: 1.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      Profile
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.03)'
                            : 'rgba(0,0,0,0.02)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            Name
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCustomer.name || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            Email
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCustomer.email || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            Phone
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCustomer.phone || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            KYC Status
                          </Typography>
                          <Box sx={{ mt: 0.25 }}>
                            {renderKycChip(selectedCustomer.kycStatus)}
                          </Box>
                        </Grid>
                        {selectedCustomer.kycStatus &&
                          ['reject', 'rejected'].includes(
                            String(selectedCustomer.kycStatus)
                              .toLowerCase()
                              .trim(),
                          ) &&
                          selectedCustomer.rejectReason && (
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                              }}
                            >
                                Rejection Reason
                            </Typography>
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedCustomer.rejectReason}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            Gender
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCustomer.gender || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            Date of Birth
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedCustomer.dateOfBirth || '—'}
                          </Typography>
                        </Grid>
                        {selectedCustomer.kycType && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                              }}
                            >
                              KYC Type
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                textTransform: 'capitalize',
                              }}
                            >
                              {selectedCustomer.kycType}
                            </Typography>
                          </Grid>
                        )}
                        {selectedCustomer.aadhaarNumber && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                              }}
                            >
                              Aadhaar Number
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedCustomer.aadhaarNumber}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            Account Status
                          </Typography>
                          <Box sx={{ mt: 0.25 }}>
                            <Chip
                              label={
                                selectedCustomer.isActive
                                  ? 'Active'
                                  : 'Inactive'
                              }
                              size="small"
                              sx={{
                                height: 22,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                bgcolor: selectedCustomer.isActive
                                  ? 'rgba(46, 125, 50, 0.12)'
                                  : theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.08)'
                                    : 'rgba(0,0,0,0.06)',
                                color: selectedCustomer.isActive
                                  ? 'success.main'
                                  : 'text.secondary',
                                border: 'none',
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>

                  {/* Address section */}
                  {(selectedCustomer.address1 ||
                    selectedCustomer.address2 ||
                    selectedCustomer.pincode ||
                    selectedCustomer.careOf ||
                    selectedCustomer.house ||
                    selectedCustomer.street ||
                    selectedCustomer.city ||
                    selectedCustomer.district ||
                    selectedCustomer.state ||
                    selectedCustomer.country) && (
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          display: 'block',
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          color: 'text.secondary',
                          mb: 1.5,
                          fontSize: '0.7rem',
                        }}
                      >
                        Address
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.03)'
                              : 'rgba(0,0,0,0.02)',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Grid container spacing={2}>
                          {selectedCustomer.careOf && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                Care Of
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.careOf}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.house && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                House / Building
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.house}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.street && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                Street
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.street}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.address1 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                Address Line 1
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.address1}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.address2 && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                Address Line 2
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.address2}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.city && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                City / Locality
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.city}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.district && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                District
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.district}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.state && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                State
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.state}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.pincode && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                Pincode
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.pincode}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.country && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  display: 'block',
                                }}
                              >
                                Country
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {selectedCustomer.country}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Box>
                  )}

                  {/* Date section */}
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        color: 'text.secondary',
                        mb: 1.5,
                        fontSize: '0.7rem',
                      }}
                    >
                      Date Information
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.03)'
                            : 'rgba(0,0,0,0.02)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'block',
                            }}
                          >
                            Account Created
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatToIST(selectedCustomer.createdOn)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'block',
                            }}
                          >
                            Account Updated
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatToIST(selectedCustomer.updatedOn)}
                          </Typography>
                        </Grid>
                        {selectedCustomer.kycCreatedOn && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                              }}
                            >
                              KYC Submitted
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {formatToIST(selectedCustomer.kycCreatedOn)}
                            </Typography>
                          </Grid>
                        )}
                        {selectedCustomer.kycUpdatedOn && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                              }}
                            >
                              KYC Last Updated
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {formatToIST(selectedCustomer.kycUpdatedOn)}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Box>

                  {/* KYC Photo: DigiLocker uses base64 in json; manual KYC uses signed URLs */}
                  {(selectedCustomer.base64Image ||
                    selectedCustomer.aadharFrontUrl ||
                    selectedCustomer.aadharBackUrl ||
                    selectedCustomer.userImageUrl) && (
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          display: 'block',
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          color: 'text.secondary',
                          mb: 1.5,
                          fontSize: '0.7rem',
                        }}
                      >
                        {selectedCustomer.base64Image
                          ? 'KYC Photo'
                          : 'KYC Documents'}
                      </Typography>
                      {selectedCustomer.base64Image ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.03)'
                                : 'rgba(0,0,0,0.02)',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box
                            component="img"
                            src={`data:image/jpeg;base64,${selectedCustomer.base64Image}`}
                            alt="KYC Photo"
                            sx={{
                              maxWidth: '200px',
                              maxHeight: '200px',
                              objectFit: 'contain',
                              borderRadius: 2,
                              boxShadow:
                                theme.palette.mode === 'dark'
                                  ? '0 4px 12px rgba(0,0,0,0.3)'
                                  : '0 4px 12px rgba(0,0,0,0.08)',
                            }}
                          />
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          {[
                            {
                              url: selectedCustomer.aadharFrontUrl,
                              label: 'Aadhaar — Front',
                            },
                            {
                              url: selectedCustomer.aadharBackUrl,
                              label: 'Aadhaar — Back',
                            },
                            {
                              url: selectedCustomer.userImageUrl,
                              label: 'Selfie verification',
                            },
                          ]
                            .filter((item) => item.url)
                            .map((item) => (
                              <Grid item xs={12} sm={4} key={item.label}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: 'block',
                                    mb: 1,
                                    fontWeight: 600,
                                  }}
                                >
                                  {item.label}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor:
                                      theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.03)'
                                        : 'rgba(0,0,0,0.02)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={item.url}
                                    alt={item.label}
                                    sx={{
                                      maxWidth: '100%',
                                      maxHeight: '200px',
                                      width: 'auto',
                                      objectFit: 'contain',
                                      borderRadius: 2,
                                      boxShadow:
                                        theme.palette.mode === 'dark'
                                          ? '0 4px 12px rgba(0,0,0,0.3)'
                                          : '0 4px 12px rgba(0,0,0,0.08)',
                                    }}
                                  />
                                </Box>
                              </Grid>
                            ))}
                        </Grid>
                      )}
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  px: { xs: 2.5, sm: 3 },
                  py: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleModalClose}
                  sx={{
                    bgcolor: colors.swiftcarry[100],
                    color: '#fff',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2.5,
                    py: 1,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                      boxShadow: '0 4px 12px rgba(0, 201, 150, 0.35)',
                      filter: 'brightness(1.05)',
                    },
                  }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Manual KYC Review Modal */}
      <Modal
        open={kycModalOpen}
        onClose={() => {
          setKycModalOpen(false);
          resetImageControls();
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            maxWidth: '1200px',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {selectedCustomer && kycDocuments && (
            <>
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid #e0e0e0',
                  position: 'relative',
                }}
              >
                <IconButton
                  onClick={() => {
                    setKycModalOpen(false);
                    resetImageControls();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: 'text.secondary',
                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                      color: 'text.primary',
                    },
                    zIndex: 1,
                  }}
                  aria-label="close"
                >
                  <Close />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  Manual KYC Review -{' '}
                  {selectedCustomer.json?.name ||
                    selectedCustomer.name ||
                    'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>KYC ID:</strong> {selectedCustomer.kycId || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>User ID:</strong> {selectedCustomer.userId || 'N/A'}
                  </Typography>
                  {selectedCustomer.clientId != null &&
                    selectedCustomer.clientId !== '' && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Client ID:</strong> {selectedCustomer.clientId}
                    </Typography>
                  )}
                  {selectedCustomer.type && (
                    <Chip
                      label={selectedCustomer.type}
                      size="small"
                      sx={{
                        bgcolor:
                          selectedCustomer.type === 'manual'
                            ? colors.blueAccent[100]
                            : colors.greenAccent[100],
                        color: 'white',
                        height: 24,
                      }}
                    />
                  )}
                  {selectedCustomer.status &&
                    renderKycStatusChip(selectedCustomer.status, 'small')}
                </Box>
                {(selectedCustomer.createdOn || selectedCustomer.updatedOn) && (
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}
                  >
                    {selectedCustomer.createdOn && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Created:</strong>{' '}
                        {formatToIST(selectedCustomer.createdOn)}
                      </Typography>
                    )}
                    {selectedCustomer.updatedOn && (
                      <Typography variant="caption" color="text.secondary">
                        <strong>Updated:</strong>{' '}
                        {formatToIST(selectedCustomer.updatedOn)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      📄 KYC Details
                    </Typography>

                    {/* Personal Information */}
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: '#f0f8ff',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}
                      >
                        👤 Personal Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Name:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.json?.name ||
                              selectedCustomer.name ||
                              'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Aadhaar Number:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.aadhaarNumber || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Date of Birth:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.json?.dob ||
                              selectedCustomer.dateOfBirth ||
                              'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>Gender:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1.5 }}>
                            {selectedCustomer.json?.gender?.toUpperCase() ===
                            'M'
                              ? 'Male'
                              : selectedCustomer.json?.gender?.toUpperCase() ===
                                'F'
                                ? 'Female'
                                : selectedCustomer.json?.gender || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Address Details */}
                    {selectedCustomer.json &&
                      (selectedCustomer.json.address1 ||
                        selectedCustomer.json.address2 ||
                        selectedCustomer.json.pincode) && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          bgcolor: '#fffbf0',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}
                        >
                            📍 Address Details
                        </Typography>
                        <Grid container spacing={2}>
                          {(selectedCustomer.json.address1 ||
                              selectedCustomer.json.address2) && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>Address:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {[
                                  selectedCustomer.json.address1,
                                  selectedCustomer.json.address2,
                                ]
                                  .filter(Boolean)
                                  .join(', ') || 'N/A'}
                              </Typography>
                            </Grid>
                          )}
                          {selectedCustomer.json.pincode && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>Pincode:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {selectedCustomer.json.pincode}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {/* Account Details */}
                    {(selectedCustomer.email || selectedCustomer.phoneNo) && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          bgcolor: '#f9f9f9',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}
                        >
                          📧 Account Details
                        </Typography>
                        <Grid container spacing={2}>
                          {selectedCustomer.email && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>Email:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {selectedCustomer.email}
                              </Typography>
                            </Grid>
                          )}
                          {(selectedCustomer.phoneNo ||
                            selectedCustomer.phone) && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                <strong>Phone:</strong>
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                {selectedCustomer.phoneNo ||
                                  selectedCustomer.phone ||
                                  'N/A'}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {/* Aadhaar Verification */}
                    <Box
                      sx={{
                        mb: 3,
                        p: 3,
                        border: '2px solid #e3f2fd',
                        borderRadius: 3,
                        bgcolor:
                          'linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%)',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: colors.swiftcarry[100],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                          }}
                        >
                          <Typography sx={{ color: 'white', fontSize: '16px' }}>
                            🆔
                          </Typography>
                        </Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: '#1976d2' }}
                        >
                          Aadhaar Verification
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <TextField
                          value={aadhaarNumber}
                          onChange={(e) => {
                            const formatted = formatAadhaar(e.target.value);
                            if (formatted.replace(/\s/g, '').length <= 12) {
                              setAadhaarNumber(formatted);
                              setAadhaarError('');
                            }
                          }}
                          placeholder="XXXX XXXX XXXX"
                          error={!!aadhaarError}
                          helperText={aadhaarError}
                          size="small"
                          inputProps={{
                            maxLength: 14,
                            style: {
                              fontFamily: 'monospace',
                              fontSize: '14px',
                              letterSpacing: '1px',
                            },
                          }}
                          sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white',
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: '#e0e0e0',
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                            },
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={handleAadhaarVerify}
                          disabled={!aadhaarNumber.trim()}
                          sx={{
                            bgcolor: colors.swiftcarry[100],
                            minWidth: 100,
                            height: 40,
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                            '&:hover': {
                              bgcolor: colors.swiftcarry[100],
                              boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                              transform: 'translateY(-1px)',
                            },
                            '&:disabled': {
                              bgcolor: '#e0e0e0',
                              color: '#999',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          Verify
                        </Button>
                      </Box>
                    </Box>

                    {aadhaarVerified && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #4caf50',
                          borderRadius: 2,
                          bgcolor: '#e8f5e9',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: '#2e7d32' }}
                        >
                          ✓ Aadhaar verified
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Verified number: {aadhaarNumber || 'N/A'}
                        </Typography>
                      </Box>
                    )}

                    {/* Previous Rejection Reason */}
                    {selectedCustomer.rejectReason && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '2px solid #ff9800',
                          borderRadius: 2,
                          bgcolor: '#fff3e0',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}
                        >
                          ⚠️ Previous Rejection Reason
                        </Typography>
                        <Typography sx={{ fontSize: '13px' }}>
                          {selectedCustomer.rejectReason}
                        </Typography>
                      </Box>
                    )}

                    {/* Rejection Reason */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        🚫 Rejection Reason (if applicable)
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={rejectionCategory}
                        onChange={(e) => setRejectionCategory(e.target.value)}
                        label="Select rejection category"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: '#ffffffff',
                          },
                        }}
                      >
                        <DropdownItem value="">None - Approve KYC</DropdownItem>
                        <DropdownItem value="Document Issue">
                          Document Issue
                        </DropdownItem>
                        <DropdownItem value="Data Mismatch">
                          Data Mismatch
                        </DropdownItem>
                        <DropdownItem value="Quality Issue">
                          Quality Issue
                        </DropdownItem>
                        <DropdownItem value="Other">Other</DropdownItem>
                      </TextField>

                      {rejectionCategory && rejectionCategory !== '' && (
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={rejectionReason}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= 500) {
                                setRejectionReason(value);
                              }
                            }}
                            placeholder="Enter detailed rejection reason (max 500 characters)"
                            variant="outlined"
                            size="small"
                            inputProps={{ maxLength: 500 }}
                            helperText={`${rejectionReason.length}/500 characters`}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: '#ffffffff',
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Right Side - Image Viewer */}
                  <Grid item xs={12} md={6}>
                    {/* Image Navigation */}
                    {kycDocuments?.documentImages &&
                      kycDocuments.documentImages.length > 1 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, textAlign: 'center', mb: 1 }}
                        >
                          {
                            kycDocuments.documentImages[currentImageIndex]
                              ?.label
                          }
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <IconButton
                            onClick={prevImage}
                            sx={{ bgcolor: '#f5f5f5' }}
                          >
                            <ArrowBack />
                          </IconButton>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                          >
                            {currentImageIndex + 1} of{' '}
                            {kycDocuments.documentImages.length}
                          </Typography>
                          <IconButton
                            onClick={nextImage}
                            sx={{ bgcolor: '#f5f5f5' }}
                          >
                            <ArrowForward />
                          </IconButton>
                        </Box>
                      </Box>
                    )}

                    {/* Document Image */}
                    <Box
                      sx={{
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        p: 1,
                        textAlign: 'center',
                        minHeight: 300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                    >
                      {kycDocuments?.documentImages &&
                      kycDocuments.documentImages[currentImageIndex] ? (
                          <img
                            src={
                              kycDocuments.documentImages[currentImageIndex].url
                            }
                            alt={
                              kycDocuments.documentImages[currentImageIndex].label
                            }
                            style={{
                              maxWidth: '100%',
                              maxHeight: '400px',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <Typography color="text.secondary">
                          No document images available
                          </Typography>
                        )}

                      {/* Image overlay navigation for mobile */}
                      {kycDocuments?.documentImages &&
                        kycDocuments.documentImages.length > 1 && (
                        <>
                          <IconButton
                            onClick={prevImage}
                            sx={{
                              position: 'absolute',
                              left: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            }}
                          >
                            <ArrowBack />
                          </IconButton>
                          <IconButton
                            onClick={nextImage}
                            sx={{
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              bgcolor: 'rgba(0,0,0,0.5)',
                              color: 'white',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                            }}
                          >
                            <ArrowForward />
                          </IconButton>
                        </>
                      )}
                    </Box>

                    {/* Image Thumbnails */}
                    {kycDocuments?.documentImages &&
                      kycDocuments.documentImages.length > 1 && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          mt: 2,
                          justifyContent: 'center',
                        }}
                      >
                        {kycDocuments.documentImages.map((img, index) => (
                          <Box
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            sx={{
                              width: 60,
                              height: 40,
                              border:
                                  currentImageIndex === index
                                    ? `2px solid ${colors.swiftcarry[100]}`
                                    : '1px solid #ccc',
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 },
                            }}
                          >
                            <img
                              src={img.url}
                              alt={img.label}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setKycModalOpen(false);
                      resetImageControls();
                    }}
                    sx={{ minWidth: 120 }}
                  >
                    Cancel
                  </Button>
                  {rejectionCategory && rejectionCategory !== '' ? (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleKycRejection}
                      disabled={loading}
                      sx={{ minWidth: 120 }}
                    >
                      Reject KYC
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleKycApproval}
                      disabled={loading}
                      sx={{
                        bgcolor: colors.swiftcarry[100],
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        '&:hover': {
                          bgcolor: colors.swiftcarry[100],
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        },
                        minWidth: 120,
                      }}
                    >
                      Approve KYC
                    </Button>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default Customer;
